import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { newsApi } from "../services/newsApi";

type NewsItem = {
	id: string;
	title: string;
	description: string;
	type: "crypto" | "trump";
	icon: string;
	date: string;
};

const INITIAL_NEWS: NewsItem[] = [
	{
		id: "1",
		title: "$TRUMP Token Launch Soon! 🚀",
		description: "Get ready for the most epic token launch on SUI Network!",
		type: "crypto",
		icon: "🎉",
		date: "Just now",
	},
	{
		id: "2",
		title: "Community Growing Strong! 💪",
		description: "Join our Telegram and Twitter communities!",
		type: "trump",
		icon: "👥",
		date: "2h ago",
	},
	{
		id: "3",
		title: "SUI Network Update",
		description: "New features coming to boost your gains!",
		type: "crypto",
		icon: "📈",
		date: "4h ago",
	},
];

const NewsPanel = () => {
	const [news, setNews] = useState<NewsItem[]>(INITIAL_NEWS);
	const [selectedType, setSelectedType] = useState<"all" | "crypto" | "trump">("all");
	const [loading, setLoading] = useState(false);

	const fetchNews = useCallback(async () => {
		setLoading(true);
		try {
			const [cryptoNews, trumpNews] = await Promise.all([
				newsApi.getCryptoNews(),
				newsApi.getTrumpNews(),
			]);

			const combinedNews = [...cryptoNews, ...trumpNews]
				.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
				.slice(0, 4);

			setNews(combinedNews);
		} catch (error) {
			console.error("Error fetching news:", error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchNews();
		const interval = setInterval(fetchNews, 300000); // 5 minutos
		return () => clearInterval(interval);
	}, [fetchNews]);

	const filteredNews = news.filter(
		(item) => selectedType === "all" || item.type === selectedType
	);

	return (
		<div className='flex flex-col h-full'>
			<div className='flex items-center justify-between mb-4'>
				<div className='flex items-center gap-2'>
					<h2 className='text-3xl font-black text-trump-yellow'>NEWS</h2>
					<div className='relative group'>
						<span className='cursor-help text-white/60 hover:text-white'>ℹ️</span>
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							className='absolute left-0 top-full mt-2 w-64 p-4 bg-black/90 rounded-xl shadow-xl z-10 hidden group-hover:block'>
							<p className='text-white text-sm'>
								Real-time news feed integration coming in the next update! 📰
							</p>
						</motion.div>
					</div>
				</div>
				<div className='flex gap-2'>
					{["all", "crypto", "trump"].map((type) => (
						<button
							key={type}
							onClick={() => setSelectedType(type as typeof selectedType)}
							className={`px-3 py-1 rounded-full text-sm font-bold transition-colors ${
								selectedType === type
									? "bg-trump-yellow text-black"
									: "bg-white/10 text-white hover:bg-white/20"
							}`}>
							{type.toUpperCase()}
						</button>
					))}
				</div>
			</div>

			<div className='space-y-3 flex-1 overflow-y-auto custom-scrollbar'>
				{loading ? (
					<div className='space-y-4'>
						{[1, 2, 3].map((i) => (
							<div key={i} className='animate-pulse'>
								<div className='h-20 bg-white/10 rounded-xl' />
							</div>
						))}
					</div>
				) : (
					<AnimatePresence>
						{filteredNews.map((item) => (
							<motion.div
								key={item.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								className='bg-black/30 p-4 rounded-xl'>
								<div className='flex items-start gap-3'>
									<span className='text-2xl'>{item.icon}</span>
									<div className='flex-1'>
										<h3 className='font-bold text-white'>{item.title}</h3>
										<p className='text-white/80 text-sm'>{item.description}</p>
										<span className='text-trump-yellow text-xs'>
											{item.date}
										</span>
									</div>
								</div>
							</motion.div>
						))}
					</AnimatePresence>
				)}
			</div>
		</div>
	);
};

export default NewsPanel;
